/**
 * Modals for saving, viewing, and deleting public preview artifacts.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { preparePortableState } from '../core/save-load.js';
import { versionContentHashOf } from '../shared/version-hash.js';
import { formatBytes } from '../shared/format.js';
// NOT a static import (TL-2). `shell.js` statically imports this module, and
// `preview-bundler.js` pulls in `export/exporter.js` and the rest of the export
// graph — so a static specifier here put the whole exporter on the BOOT path,
// which is the one thing `builder/CLAUDE.md`'s deploy-bundle section is about.
// It went unnoticed because boot-tests listed `../export/exporter.js` as a
// forbidden DIRECT import of shell.js, and it was never direct: shell.js →
// preview-modal.js → preview-bundler.js → exporter.js. A transitive walk is
// the only detector, and boot-tests has one now.
//
// `review-shell.js:250` and `project-thumbnail.js:93` already load it this way.
import { generatePreviewSlug } from '../preview/preview-slug.js';
import {
  checkPreviewStatus,
  deletePreview,
  listPreviews,
  PreviewApiError,
  savePreview
} from '../preview/preview-api.js';
import { showConfirmModal, showToast } from './shell.js';
import { createProgressModal, createButton } from './components.js';
import { openCollectionBrowser, rememberedCount, rememberCount } from './collection-browser.js';
import { copyTextToClipboard } from './components.js';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;
const LAST_SAVED_PREVIEW_KEY = 'pb:last-saved-preview';

function clear(el) {
  el.replaceChildren();
}

function readLastSavedPreview(projectName = null) {
  try {
    const raw = sessionStorage.getItem(LAST_SAVED_PREVIEW_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.result?.slug || !cached.result.previewUrl) return null;
    if (projectName && cached.projectName && cached.projectName !== projectName) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeLastSavedPreview(result, status, stats, projectName) {
  try {
    sessionStorage.setItem(
      LAST_SAVED_PREVIEW_KEY,
      JSON.stringify({
        result,
        status,
        stats,
        projectName,
        savedAt: new Date().toISOString()
      })
    );
  } catch {
    // Session restore is a convenience; preview save itself has already worked.
  }
}

function clearLastSavedPreview(slug = null) {
  try {
    if (!slug) {
      sessionStorage.removeItem(LAST_SAVED_PREVIEW_KEY);
      return;
    }
    const cached = readLastSavedPreview();
    if (cached?.result?.slug === slug) {
      sessionStorage.removeItem(LAST_SAVED_PREVIEW_KEY);
    }
  } catch {
    // Ignore unavailable storage.
  }
}

function createUrlRow(label, url, { pending = false, error = '' } = {}) {
  const row = document.createElement('div');
  row.style.display = 'grid';
  row.style.gridTemplateColumns = 'minmax(0, 1fr) auto auto';
  row.style.gap = '0.5rem';
  row.style.alignItems = 'center';

  const text = document.createElement('div');
  text.style.minWidth = '0';
  const strong = document.createElement('strong');
  strong.textContent = label;
  const value = document.createElement('p');
  value.className = 'pb-confirm-detail';
  value.style.margin = '0.25rem 0 0';
  value.style.overflowWrap = 'anywhere';
  value.textContent = error || url || (pending ? 'Generating...' : '');
  text.append(strong, value);

  const copy = createButton({
    variant: 'ghost',
    label: 'Copy',
    disabled: !url || !!error,
    onClick: async () => {
      const copied = await copyTextToClipboard(url);
      copy.textContent = copied ? 'Copied' : 'Copy failed';
      setTimeout(() => {
        copy.textContent = 'Copy';
      }, 1500);
    }
  });

  const canOpen = Boolean(url && !pending && !error);
  const open = createButton({
    variant: 'ghost',
    label: 'Open',
    disabled: !canOpen,
    onClick: () => {
      if (canOpen) window.open(url, '_blank', 'noopener,noreferrer');
    }
  });

  row.append(text, copy, open);
  return row;
}

function renderSaveResult(body, footer, result, status, stats, options = {}) {
  clear(body);
  clear(footer);

  const wrap = document.createElement('div');
  wrap.style.display = 'grid';
  wrap.style.gap = '1rem';

  const note = document.createElement('p');
  note.className = 'pb-confirm-msg';
  note.textContent =
    'Treat this URL as private until the prospect confirms. Anyone with the link can view the preview. Forms are visual-only in previews.';
  wrap.appendChild(note);

  if (stats?.sizeBytes) {
    const meta = document.createElement('p');
    meta.className = 'pb-confirm-detail';
    meta.textContent = `Bundle size: ${formatBytes(stats.sizeBytes)}`;
    wrap.appendChild(meta);
  }

  if (options.restored) {
    const restored = document.createElement('p');
    restored.className = 'pb-confirm-detail';
    restored.textContent = 'Last saved preview from this browser session.';
    wrap.appendChild(restored);
  }

  wrap.appendChild(createUrlRow('Preview URL', result.previewUrl));
  const screenshotReady = status?.screenshotStatus === 'ready';
  const screenshotFailed = status?.screenshotStatus === 'failed';
  const screenshotPending = !screenshotReady && !screenshotFailed;
  wrap.appendChild(
    createUrlRow('Screenshot URL', result.screenshotUrl, {
      pending: screenshotPending,
      error: screenshotFailed
        ? status.screenshotError || 'Screenshot failed. Save again to retry.'
        : ''
    })
  );

  // The poll ran up to POLL_TIMEOUT_MS with nothing on screen changing, so a
  // slow screenshot was indistinguishable from a stuck one. pollStatus's onTick
  // writes the remaining seconds here.
  if (screenshotPending) {
    const wait = document.createElement('p');
    wait.className = 'pb-confirm-detail';
    wait.dataset.role = 'shot-wait';
    wait.setAttribute('aria-live', 'polite');
    wait.textContent = 'Screenshot usually appears in a few seconds.';
    wrap.appendChild(wait);
  }

  body.appendChild(wrap);

  const closeBtn = createButton({
    variant: 'ghost',
    label: 'Close',
    onClick: () => document.querySelector('.pb-preview-overlay .pb-modal-close')?.click()
  });
  const retryBtn = createButton({
    variant: 'primary',
    label: 'Save New Preview',
    onClick: () => {
      document.querySelector('.pb-preview-overlay .pb-modal-close')?.click();
      showSavePreviewModal({ forceNew: true });
    }
  });
  footer.append(closeBtn, retryBtn);
}

function abortError() {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

async function saveWithFreshSlug(bundle, projectName, sourceTemplateDomain, signal = null) {
  const live = store.getState();
  const baseArgs = {
    html: bundle.html,
    projectName,
    sourceTemplateDomain,
    metaTitle: bundle.metaTitle,
    metaDescription: bundle.metaDescription,
    ogImage: bundle.ogImage,
    // Best-effort project linkage (additive): cloudVersion may trail the save
    // the publish hook is about to mint — the publish log row is authoritative.
    projectId: live?.projectId || null,
    projectVersion: Number(live?.cloudVersion) || null,
    signal
  };
  let lastErr = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    // Checked per attempt, not once up front: a 409 retry re-POSTs the whole
    // bundle, so without this a cancel during attempt 1 still sent attempts 2
    // and 3 — the exact "closing the modal doesn't stop the upload" bug.
    if (signal?.aborted) throw abortError();
    const slug = generatePreviewSlug(projectName);
    try {
      return await savePreview({ slug, ...baseArgs });
    } catch (err) {
      lastErr = err;
      if (!(err instanceof PreviewApiError) || err.status !== 409) throw err;
    }
  }
  throw lastErr || new Error('Could not generate a unique preview slug.');
}

function delay(ms, signal = null) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(abortError());
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * @param {(remainingMs: number) => void} [onTick] — called each interval with the
 *   time left before the poll gives up, so the modal can say so instead of
 *   sitting silent for the full 20 s.
 */
async function pollStatus(slug, shouldStop, onStatus, { signal = null, onTick } = {}) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let latest = null;
  while (!shouldStop() && Date.now() < deadline) {
    onTick?.(deadline - Date.now());
    await delay(POLL_INTERVAL_MS, signal);
    if (shouldStop()) break;
    latest = await checkPreviewStatus(slug, { signal });
    onStatus(latest);
    if (latest.screenshotStatus === 'ready' || latest.screenshotStatus === 'failed') break;
  }
  return latest;
}

export function showSavePreviewModal({ forceNew = false } = {}) {
  // Save Preview used to be the longest UNCANCELLABLE operation in the app:
  // closing the modal flipped a local `closed` flag while the bundle, the POST
  // and the 20s status poll all ran on. CK-4 gave template upload exactly this
  // treatment and never came back for this file. The kit's progress modal owns
  // the abort contract now — every user dismissal fires onCancel.
  const controller = new AbortController();
  const signal = controller.signal;
  const progress = createProgressModal({
    title: 'Save Preview',
    phase: 'Bundling…',
    maxWidth: 620,
    overlayClassName: 'pb-preview-overlay',
    onCancel: () => controller.abort()
  });
  const { body, footer, isClosed } = progress;
  const state = store.getState();
  const projectName = state?.projectName || 'Preview';
  const sourceTemplateDomain =
    typeof state?.sourceTemplateDomain === 'string' ? state.sourceTemplateDomain.trim() : '';

  if (!forceNew) {
    const cached = readLastSavedPreview(projectName);
    if (cached) {
      renderSaveResult(body, footer, cached.result, cached.status || cached.result, cached.stats, {
        restored: true
      });
      return;
    }
  }

  progress.setPhase('Bundling…', 'Inlining homepage images, fonts, CSS, and scripts.');

  // FN-8: the async body is RETURNED, not orphaned. A caller needs to know when
  // this settles to hold a single-flight guard over it; without that the guard
  // released on the synchronous return and a second click started a second
  // bundle, a second upload and a second progress dialog.
  return (async () => {
    // Identity of the state this preview ships — captured against the SAME
    // `state` reference the bundler is about to clone, so an edit landing
    // during the multi-second bundle/upload cannot be recorded as published
    // (the hook refuses versions whose hash differs).
    const shippedHash = await versionContentHashOf(preparePortableState(state)).catch(() => null);
    const { buildPreviewBundle } = await import('../preview/preview-bundler.js');
    const bundle = await buildPreviewBundle(state, {
      signal,
      onProgress: ({ message, percent }) => {
        progress.setPhase(message);
        progress.setProgress(percent / 100);
      }
    });
    if (isClosed()) return;

    // The upload itself reports no byte progress, so it drops to indeterminate
    // rather than parking the bar at the bundle's final percentage — a frozen
    // 100% bar is a worse lie than an honest sweep.
    progress.setProgress(null);
    progress.setPhase('Uploading…', `${formatBytes(bundle.stats.sizeBytes)} preview bundle ready.`);
    const result = await saveWithFreshSlug(bundle, projectName, sourceTemplateDomain, signal);
    if (isClosed()) return;

    const initialStatus = { screenshotStatus: result.screenshotStatus };
    writeLastSavedPreview(result, initialStatus, bundle.stats, projectName);
    // Publish seam (migration 006): the modal owns the success moment, shell
    // owns the cloud-save + publish-log write. Fire-and-forget by design —
    // the preview link already works whatever the hook does.
    eventBus.emit('preview:saved', { slug: result.slug, projectName, contentHash: shippedHash });
    // The URL works from here on, so the dialog stops being a progress surface
    // and becomes the result — same body and footer, no second modal.
    // (renderSaveResult clears both.)
    renderSaveResult(body, footer, result, initialStatus, bundle.stats);

    await pollStatus(
      result.slug,
      // Was hardcoded `() => false`: the poll ran the full 20s timeout even
      // after the user closed the modal, holding the screenshot request open
      // against a dialog that no longer existed.
      () => isClosed() || signal.aborted,
      (status) => {
        writeLastSavedPreview(result, status, bundle.stats, projectName);
        if (!isClosed()) renderSaveResult(body, footer, result, status, bundle.stats);
      },
      {
        signal,
        onTick: (remainingMs) => {
          if (isClosed()) return;
          const el = body.querySelector('[data-role="shot-wait"]');
          if (el) el.textContent = `Still generating… ${Math.ceil(remainingMs / 1000)}s left`;
        }
      }
    );
  })().catch((err) => {
    // A cancel is not a failure: the user already closed the dialog, so there is
    // nothing to report and nowhere to report it.
    if (err?.name === 'AbortError' || isClosed()) return;
    clear(body);
    clear(footer);
    const msg = document.createElement('p');
    msg.className = 'pb-confirm-msg';
    msg.textContent = err?.message || 'Preview save failed.';
    body.appendChild(msg);
    footer.appendChild(
      createButton({
        variant: 'primary',
        label: 'Close',
        onClick: () => progress.close()
      })
    );
  });
}

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value));
  } catch {
    return value || '';
  }
}

function previewUrlFor(preview) {
  return preview.previewUrl || (preview.slug ? `/preview/${encodeURIComponent(preview.slug)}` : '');
}

function formatScreenshotStatus(status) {
  if (status === 'ready') return 'thumbnail saved';
  if (status === 'failed') return 'live thumbnail shown';
  return 'generating thumbnail';
}

function formatTemplateLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.startsWith('custom:')) {
    const label = raw.slice(7).replace(/[-_]+/g, ' ').trim();
    return label ? `Custom: ${label}` : 'Custom template';
  }
  try {
    const parsed = raw.includes('://') ? new URL(raw) : new URL(`https://${raw}`);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return raw.replace(/^www\./, '');
  }
}

function createTitleRow(preview) {
  const titleRow = document.createElement('div');
  titleRow.style.display = 'flex';
  titleRow.style.flexWrap = 'wrap';
  titleRow.style.alignItems = 'center';
  titleRow.style.gap = '0.4rem';
  titleRow.style.minWidth = '0';

  const name = document.createElement('strong');
  name.textContent = preview.projectName || preview.slug;
  titleRow.appendChild(name);

  const templateLabel = formatTemplateLabel(preview.sourceTemplateDomain);
  if (templateLabel) {
    const badge = document.createElement('span');
    badge.textContent = `Base: ${templateLabel}`;
    badge.dataset.tooltip = `Base template: ${preview.sourceTemplateDomain}`;
    badge.style.maxWidth = '18rem';
    badge.style.overflow = 'hidden';
    badge.style.textOverflow = 'ellipsis';
    badge.style.whiteSpace = 'nowrap';
    badge.style.border = '1px solid var(--pb-border, #e5e7eb)';
    badge.style.borderRadius = '999px';
    badge.style.padding = '0.1rem 0.45rem';
    badge.style.fontSize = '0.72rem';
    badge.style.fontWeight = '700';
    badge.style.color = 'var(--pb-text-dim, #6b7280)';
    badge.style.background = 'var(--pb-surface-hover, #f3f4f6)';
    titleRow.appendChild(badge);
  }

  return titleRow;
}

/**
 * The live-preview fallback, poster-sized (CB-6): a sandboxed iframe scaled to
 * the poster box by a ResizeObserver the browser handle owns and disconnects
 * on close. allow-scripts WITHOUT allow-same-origin — granting both would let
 * the framed document reach into this origin, which is the whole point of the
 * sandbox.
 */
function createLivePoster(url, registerObserver) {
  const wrap = document.createElement('div');
  wrap.style.position = 'absolute';
  wrap.style.inset = '0';
  wrap.style.overflow = 'hidden';
  wrap.style.background = '#fff';
  if (!url) {
    wrap.style.display = 'grid';
    wrap.style.placeItems = 'center';
    wrap.style.fontSize = '0.75rem';
    wrap.style.color = 'var(--pb-text-dim, #6b7280)';
    wrap.textContent = 'Pending';
    return wrap;
  }
  const frame = document.createElement('iframe');
  frame.src = url;
  frame.loading = 'lazy';
  frame.tabIndex = -1;
  frame.title = '';
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.style.width = '480px';
  frame.style.height = '300px';
  frame.style.border = '0';
  frame.style.transformOrigin = 'top left';
  frame.style.pointerEvents = 'none';
  const rescale = () => {
    const width = wrap.clientWidth || 220;
    frame.style.transform = `scale(${(width / 480).toFixed(4)})`;
  };
  rescale();
  if (typeof ResizeObserver === 'function' && registerObserver) {
    const observer = new ResizeObserver(rescale);
    observer.observe(wrap);
    registerObserver(observer);
  }
  wrap.appendChild(frame);
  return wrap;
}

/**
 * Poster content per screenshot rung. The error listener is the middle rung:
 * `ready` with a URL that 404s must ALSO fall back to the live iframe, or the
 * user gets a dead box on a preview that renders perfectly well.
 */
function createPosterContent(preview, registerObserver) {
  if (preview.screenshotStatus === 'ready' && preview.screenshotUrl) {
    const img = document.createElement('img');
    img.src = preview.screenshotUrl;
    img.alt = '';
    img.loading = 'lazy';
    img.addEventListener(
      'error',
      () => {
        img.replaceWith(createLivePoster(previewUrlFor(preview), registerObserver));
      },
      { once: true }
    );
    return img;
  }
  return createLivePoster(previewUrlFor(preview), registerObserver);
}

export function showMyPreviewsModal() {
  let api = null;
  const registerObserver = (observer) => api?.addObserver(observer);

  async function deleteItems(items) {
    const targets = items.filter((item) => item?.slug);
    if (!targets.length) return;
    const label =
      targets.length === 1
        ? `"${targets[0].projectName || targets[0].slug}"`
        : `${targets.length} previews`;
    const confirmed = await showConfirmModal({
      title: targets.length === 1 ? 'Delete Preview' : 'Delete Previews',
      message: `Delete ${label}?`,
      details: 'This removes the public preview HTML and screenshot.',
      confirmLabel: targets.length === 1 ? 'Delete' : 'Delete Selected',
      cancelLabel: 'Cancel',
      danger: true
    });
    if (!confirmed || api.isClosed()) return;

    const results = await Promise.allSettled(targets.map((item) => deletePreview(item.slug)));
    for (let i = 0; i < results.length; i += 1) {
      // The session cache clears ONLY for deletes that actually happened — a
      // failed delete leaves the preview live, and wiping its cache entry
      // would disown a working artifact.
      if (results[i].status === 'fulfilled') {
        clearLastSavedPreview(targets[i].slug);
      }
    }
    if (api.isClosed()) return;
    await api.refresh();
    const failed = results.filter((result) => result.status === 'rejected');
    if (failed.length > 0 && !api.isClosed()) {
      showToast(
        `${failed.length} preview${failed.length === 1 ? '' : 's'} could not be deleted.`,
        'error'
      );
    }
  }

  api = openCollectionBrowser({
    title: 'My Previews',
    initialFocus: 'search', // PP-2: search owns focus at time zero, through the kit
    testid: 'my-previews',
    size: 'wide',
    overlayClassName: 'pb-preview-overlay',
    layout: 'grid',
    listTag: 'ul',
    listAriaLabel: 'Saved previews',
    search: { placeholder: 'Search previews…', ariaLabel: 'Search previews' },
    chips: false,
    skeleton: { variant: 'project', count: () => rememberedCount('pb:previews-last-count') },
    illustration: 'previews',
    emptyCopy: {
      title: 'No saved previews yet.',
      body: 'Save a preview to get a shareable link that shows the site as it looks right now.'
    },
    filteredEmptyCopy: { title: 'No previews match this search.' },
    errorCopy: { title: 'Could not load previews.' },

    load: async () => {
      const response = await listPreviews();
      const previews = Array.isArray(response?.previews) ? response.previews : [];
      rememberCount('pb:previews-last-count', previews.length);
      return previews;
    },
    idOf: (preview, i) => preview.slug || `row-${i}`,

    adapt: (preview) => {
      // The surface owns its text block: the title row (name + Base badge) and
      // the status line keep their exact pre-migration builders and wording.
      const content = document.createElement('div');
      content.className = 'pb-coll-text';
      content.style.minWidth = '0';
      const details = document.createElement('p');
      details.className = 'pb-confirm-detail';
      details.style.margin = '0.25rem 0 0';
      details.textContent = `${formatDate(preview.createdAt)} - ${formatScreenshotStatus(preview.screenshotStatus)}`;
      if (preview.screenshotError) details.dataset.tooltip = preview.screenshotError;
      content.append(createTitleRow(preview), details);
      const name = preview.projectName || preview.slug || 'preview';
      return {
        id: preview.slug || '',
        title: name,
        searchText: [preview.projectName, preview.slug, preview.sourceTemplateDomain]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
        canSelect: !!preview.slug,
        className: 'pb-preview-card',
        poster: { kind: 'custom', build: () => createPosterContent(preview, registerObserver) },
        primaryAction: { label: 'Open preview', ariaLabel: `Open ${name}` },
        actions: [{ id: 'delete', label: 'Delete', slot: 'overlay', danger: true }],
        content,
        data: preview
      };
    },

    selection: {
      noun: 'preview',
      bulkIntents: [
        {
          id: 'delete-selected',
          label: 'Delete selected',
          variant: 'danger',
          busyLabel: 'Deleting…',
          enabled: (items) => items.length > 0,
          perform: (items) => deleteItems(items.map((item) => item.data))
        }
      ]
    },

    onActivate: (item) => {
      const url = previewUrlFor(item.data);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },
    onAction: (action, item) => {
      if (action === 'delete') return deleteItems([item.data]);
    }
  });
  return api;
}

export const __previewModalTestHooks = {
  formatScreenshotStatus,
  formatTemplateLabel,
  previewUrlFor,
  // Exposed so the cancellation behaviour can be driven for real rather than
  // asserted against source text: showSavePreviewModal() cannot run headless
  // (it needs the whole store + bundler), but these two are where cancel either
  // works or silently doesn't.
  saveWithFreshSlug,
  pollStatus
};
